import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, TextField } from '@mui/material';
import { useMeetingHubContext } from '../../../MeetingHubContext';
import MeetingService from 'services/MeetingService';
import Modal from 'components/shared/Modal';
import { ModalProps } from 'store/types/ComponentProps';
import { defaultSnackbarErrorProps } from 'util/layout';

interface AddAttendeeModalProps extends ModalProps {
  onSubmit: () => void;
}

const AddAttendeeModal: React.FunctionComponent<AddAttendeeModalProps> = ({ onClose, open, onSubmit }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, attendees } = useMeetingHubContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState('');

  const handleClose = useCallback(() => {
    setEmail('');
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    setLoading(true);
    MeetingService.getAttendeeProfile(email)
      .then((profile) => {
        if (!profile) {
          enqueueSnackbar(`Profile not found`, { variant: 'error' });
          setLoading(false);
          return;
        }

        const attendeeId = profile.customer?.internalID;
        const isAlreadyAttending = attendees?.some((a) => a.id === attendeeId);

        if (isAlreadyAttending) {
          setEmail('');
          onSubmit();

          return;
        }

        MeetingService.addAttendee({
          id: attendeeId,
          meetingId: meetingId,
          name: `${profile.customer?.firstname} ${profile.customer?.lastname}`,
          email: profile.customer?.email,
          company: profile.customer?.companyName,
          affiliate: profile.affiliate?.affiliateName,
          isManuallyAdded: true,
          isVotingMember: false,
        })
          .then((attendee) => {
            enqueueSnackbar(`Added ${attendee.name}`, { variant: 'success' });
            setEmail('');
            onSubmit();
          })
          .finally(() => setLoading(false));
      })
      .catch((error: string) => {
        enqueueSnackbar(`Failed to fetch attendee profile`, defaultSnackbarErrorProps);
        console.error('Error fetching attendee profile:', error);
      })
      .finally(() => setLoading(false));
  }, [attendees, email, enqueueSnackbar, meetingId, onSubmit]);

  return (
    <Modal
      title={'Add Attendee'}
      open={open}
      maxWidth={'xs'}
      loading={loading}
      onClose={handleClose}
      onSubmit={handleSubmit}
      actions={
        <>
          <Button color={'secondary'} variant={'outlined'} disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" color={'primary'} variant={'contained'}>
            Save
          </Button>
        </>
      }
    >
      <TextField
        label={'Email'}
        required
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />
    </Modal>
  );
};

export default AddAttendeeModal;
